import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

interface UiState {
  sidebarOpen: boolean;
  activeModal: string | null;
  activeDrawer: string | null;
  activeSection: string | null;
  railPinned: boolean;
  toggleSidebar: () => void;
  setActiveSection: (id: string | null) => void;
  toggleRailPinned: () => void;
  openModal: (id: string) => void;
  closeModal: () => void;
  openDrawer: (id: string) => void;
  closeDrawer: () => void;
}

export const useUiStore = create<UiState>()(
  devtools(
    persist(
      (set) => ({
        sidebarOpen: true,
        activeModal: null,
        activeDrawer: null,
        activeSection: null,
        railPinned: false,

        toggleSidebar: () =>
          set((state) => ({ sidebarOpen: !state.sidebarOpen }), false, 'ui/toggleSidebar'),

        setActiveSection: (id) => set({ activeSection: id }, false, 'ui/setActiveSection'),

        toggleRailPinned: () =>
          set((state) => ({ railPinned: !state.railPinned }), false, 'ui/toggleRailPinned'),

        openModal: (id) => set({ activeModal: id }, false, 'ui/openModal'),
        closeModal: () => set({ activeModal: null }, false, 'ui/closeModal'),

        openDrawer: (id) => set({ activeDrawer: id }, false, 'ui/openDrawer'),
        closeDrawer: () => set({ activeDrawer: null }, false, 'ui/closeDrawer'),
      }),
      {
        name: 'ui-store',
        // Only sidebarOpen should survive a page reload.
        partialize: (state) => ({
          sidebarOpen: state.sidebarOpen,
          activeSection: state.activeSection,
          railPinned: state.railPinned,
        }),
      },
    ),
    { name: 'UiStore', enabled: import.meta.env.DEV },
  ),
);

// Typed selector hooks
export const useSidebarOpen = () => useUiStore((s) => s.sidebarOpen);
export const useActiveModal = () => useUiStore((s) => s.activeModal);
export const useActiveDrawer = () => useUiStore((s) => s.activeDrawer);
export const useActiveSection = () => useUiStore((s) => s.activeSection);
export const useRailPinned = () => useUiStore((s) => s.railPinned);
export const useUiActions = () =>
  useUiStore(
    useShallow((s) => ({
      toggleSidebar: s.toggleSidebar,
      setActiveSection: s.setActiveSection,
      toggleRailPinned: s.toggleRailPinned,
      openModal: s.openModal,
      closeModal: s.closeModal,
      openDrawer: s.openDrawer,
      closeDrawer: s.closeDrawer,
    })),
  );
